import * as React from "react";
import { useContext, useMemo } from "react";
import { Row } from "@stenajs-webui/core";
import { StatisticsByRoutePairDetailsHeader } from "./StatisticsByRoutePairDetailsHeader";
import { useRoutePairDetailsVolumeAndRatesQuery } from "@/features/statistics/hooks/UseRoutePairDetailsVolumeAndRatesQuery";
import { useRoutePairDetailsSummaryQuery } from "@/features/statistics/hooks/UseRoutePairDetailsSummaryQuery";
import { getNegotiationStatusCodes } from "../lib/transformers/NegotiationStatusFormModelTransformer";
import { Helmet } from "react-helmet";
import { useCurrencySelection } from "@/redux/profile/hooks/UseCurrencySelection";
import { Spinner } from "@stenajs-webui/elements";
import { StatisticsDetailsView } from "@/features/statistics/components/StatisticsDetailsView";
import {
  StatisticsContext,
  StatisticsContextType,
} from "@/features/statistics/StatisticsContext";
import {
  NegotiationStatusCode,
  UseRoutePairDetailsSummaryQuery,
} from "@/gql/graphql";

interface Props {
  routeId: string;
  year: number;
}

export const StatisticsByRoutePairDetails: React.FC<Props> = ({
  routeId,
  year,
}) => {
  const { handleCurrencyChange, currency } = useCurrencySelection();

  const { summary, loading } = useRoutePairDetailsSummaryQuery(routeId, year);

  return (
    <>
      <Helmet title="Route - Statistics - FPP" />
      <StatisticsByRoutePairDetailsHeader
        routeId={routeId}
        currency={currency}
        handleCurrencyChange={handleCurrencyChange}
      />

      {loading || !summary ? (
        <Row justifyContent={"center"}>
          <Spinner />
        </Row>
      ) : (
        <DetailsDataLoader
          currency={currency}
          routeId={routeId}
          summary={summary}
          year={year}
        />
      )}
    </>
  );
};

const DetailsDataLoader = ({
  currency,
  summary,
  year,
  routeId,
}: {
  year: number;
  summary: UseRoutePairDetailsSummaryQuery["statistics"]["routePairDetails"]["summary"];
  currency: string;
  routeId: string;
}) => {
  const { routePairSelectedStatuses, setRoutePairSelectedStatuses } =
    useContext(StatisticsContext) as StatisticsContextType;

  const negotiationStatusCodes = useMemo(
    () => getNegotiationStatusCodes(routePairSelectedStatuses),
    [routePairSelectedStatuses]
  );

  const {
    volumeAndRates,
    loading: volumeAndRatesLoading,
    avgMeterPrices,
  } = useRoutePairDetailsVolumeAndRatesQuery(
    routeId,
    currency,
    negotiationStatusCodes,
    year
  );

  return (
    <StatisticsDetailsView
      currency={currency}
      year={year}
      summary={summary}
      selectedNegotiationStatuses={routePairSelectedStatuses}
      setSelectedNegotiationStatuses={setRoutePairSelectedStatuses}
      volumeAndRates={volumeAndRates}
      volumeAndRatesLoading={volumeAndRatesLoading}
      averageMeterPrices={avgMeterPrices}
    />
  );
};
